import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

const LineChart = () => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  const height = 300;

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0].contentRect.width;
      setWidth(newWidth);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // D3 chart
  useEffect(() => {
    if (!width) return;

    const data = [
      { year: "2078", a: 0, b: 10 },
      { year: "2079", a: 10, b: 35 },
      { year: "2080", a: 40, b: 25 },
      { year: "2081", a: 20, b: 35 },
      { year: "2082", a: 30, b: 45 },
      { year: "2083", a: 35, b: 20 },
    ];

    const margin = { top: 30, right: 30, bottom: 40, left: 40 };

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    svg.selectAll("*").remove();

    // X scale
    const x = d3
      .scalePoint()
      .domain(data.map((d) => d.year))
      .range([margin.left, width - margin.right]);

    // Y scale (step 10)
    const maxValue = d3.max(data, (d) => Math.max(d.a, d.b))!;
    const yMax = Math.ceil(maxValue / 10) * 10;

    const y = d3
      .scaleLinear()
      .domain([0, yMax])
      .range([height - margin.bottom, margin.top]);

    // Axis
    svg
      .append("g")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(d3.axisBottom(x));

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(d3.axisLeft(y).ticks(yMax / 10));

    // Grid lines
    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(
        d3
          .axisLeft(y)
          .ticks(yMax / 10)
          .tickSize(-width + margin.left + margin.right)
          .tickFormat(() => "")
      )
      .attr("stroke-opacity", 0.1);

    // Line generators
    const lineA = d3
      .line<any>()
      .x((d) => x(d.year)!)
      .y((d) => y(d.a));

    const lineB = d3
      .line<any>()
      .x((d) => x(d.year)!)
      .y((d) => y(d.b));

    // Line A
    svg
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "var(--primary-color)")
      .attr("stroke-width", 2)
      .attr("d", lineA);

    // Line B
    svg
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "var(--primary-color)")
      .attr("stroke-width", 2)
      .attr("d", lineB);

    // Points A
    svg
      .selectAll(".dotA")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", (d) => x(d.year)!)
      .attr("cy", (d) => y(d.a))
      .attr("r", 4)
      .attr("fill", "var(--primary-color)");

    // Points B
    svg
      .selectAll(".dotB")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", (d) => x(d.year)!)
      .attr("cy", (d) => y(d.b))
      .attr("r", 4)
      .attr("fill", "var(--primary-color)");
  }, [width]);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <svg ref={svgRef}></svg>
    </div>
  );
};

export default LineChart;
